<template>
  <b-col
    cols="12"
    class="mb-4"
  >
    <b-card>
      <template #header>
        <h4 class="mb-0">
          🗺️ Análise Geográfica - Mapa do Brasil
        </h4>
      </template>

      <b-row>
        <!-- Mapa do Brasil -->
        <b-col
          cols="12"
          :lg="estadoSelecionado ? 8 : 12"
          class="mb-3 mb-lg-0"
        >
          <div class="map-container">
            <div
              v-if="loading"
              class="text-center p-5"
            >
              <b-spinner variant="primary" />
              <p class="mt-2 text-muted">
                Carregando mapa...
              </p>
            </div>
            <div
              v-else
              id="brazil-map-svg"
              v-html="svgContent"
            />
          </div>
        </b-col>

        <!-- Card de Informações do Estado -->
        <b-col
          v-if="estadoSelecionado"
          cols="12"
          lg="4"
        >
          <div class="estado-info-card">
            <div class="estado-header">
              <h3 class="estado-nome">
                {{ getNomeEstado(estadoSelecionado.estado) }}
              </h3>
              <b-badge
                variant="primary"
                pill
                class="estado-sigla"
              >
                {{ estadoSelecionado.estado }}
              </b-badge>
            </div>

            <div class="estado-dados">
              <div class="dado-item">
                <div class="dado-icon bg-light-success">
                  <feather-icon
                    icon="DollarSignIcon"
                    size="20"
                    class="text-success"
                  />
                </div>
                <div class="dado-content">
                  <small class="text-muted">Total em Vendas</small>
                  <h4 class="mb-0 text-success">
                    R$ {{ formatarMoeda(estadoSelecionado.valor_total) }}
                  </h4>
                </div>
              </div>

              <div class="dado-item">
                <div class="dado-icon bg-light-primary">
                  <feather-icon
                    icon="ShoppingCartIcon"
                    size="20"
                    class="text-primary"
                  />
                </div>
                <div class="dado-content">
                  <small class="text-muted">Total de Pedidos</small>
                  <h4 class="mb-0 text-primary">
                    {{ estadoSelecionado.total_pedidos }}
                  </h4>
                </div>
              </div>

              <div class="dado-item">
                <div class="dado-icon bg-light-info">
                  <feather-icon
                    icon="UsersIcon"
                    size="20"
                    class="text-info"
                  />
                </div>
                <div class="dado-content">
                  <small class="text-muted">Clientes Únicos</small>
                  <h4 class="mb-0 text-info">
                    {{ estadoSelecionado.clientes_unicos }}
                  </h4>
                </div>
              </div>

              <div class="dado-item">
                <div class="dado-icon bg-light-warning">
                  <feather-icon
                    icon="TrendingUpIcon"
                    size="20"
                    class="text-warning"
                  />
                </div>
                <div class="dado-content">
                  <small class="text-muted">Ticket Médio</small>
                  <h4 class="mb-0 text-warning">
                    R$ {{ formatarMoeda(estadoSelecionado.ticket_medio) }}
                  </h4>
                </div>
              </div>
            </div>

            <b-button
              variant="outline-secondary"
              size="sm"
              block
              class="mt-3"
              @click="estadoSelecionado = null"
            >
              <feather-icon
                icon="XIcon"
                size="14"
                class="mr-50"
              />
              Fechar
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Legenda -->
      <div
        v-if="!loading"
        class="legenda-container mt-3"
      >
        <div class="legenda-header">
          <small class="font-weight-bold text-muted">Legenda: Intensidade por Valor Total</small>
        </div>
        <div class="legenda-gradient">
          <div class="gradient-bar" />
          <div class="gradient-labels">
            <span class="text-muted small">Menor</span>
            <span class="text-muted small">Maior</span>
          </div>
        </div>
      </div>

      <!-- Tooltip -->
      <div
        v-if="tooltipVisible"
        class="map-tooltip"
        :style="{ left: tooltipX + 'px', top: tooltipY + 'px' }"
      >
        <strong>{{ tooltipEstado }}</strong>
        <div
          v-if="getEstadoDados(tooltipEstado)"
          class="text-muted small"
        >
          R$ {{ formatarMoeda(getEstadoDados(tooltipEstado).valor_total) }}
        </div>
      </div>
    </b-card>
  </b-col>
</template>

<script>
import {
  BCol, BCard, BRow, BBadge, BButton, BSpinner,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { formatarMoeda } from '../services/dashboardService'

export default {
  name: 'BrazilMapAnalysis',
  components: {
    BCol,
    BCard,
    BRow,
    BBadge,
    BButton,
    BSpinner,
    FeatherIcon,
  },
  props: {
    dadosGeograficos: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: true,
      svgContent: '',
      estadoSelecionado: null,
      tooltipVisible: false,
      tooltipX: 0,
      tooltipY: 0,
      tooltipEstado: '',
    }
  },
  watch: {
    dadosGeograficos: {
      handler() {
        if (this.svgContent) {
          this.$nextTick(() => {
            this.aplicarCores()
          })
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.carregarMapa()
  },
  methods: {
    formatarMoeda,
    async carregarMapa() {
      try {
        // Importa o SVG diretamente
        const svgModule = await import('./Brazil_states.svg')
        const response = await fetch(svgModule.default || './Brazil_states.svg')
        const svgText = await response.text()
        this.svgContent = svgText
        this.loading = false

        this.$nextTick(() => {
          this.inicializarMapa()
          this.aplicarCores()
        })
      } catch (error) {
        console.error('Erro ao carregar o mapa:', error)
        this.loading = false
        this.$bvToast.toast('Erro ao carregar o mapa do Brasil', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000,
        })
      }
    },
    inicializarMapa() {
      const svgElement = document.querySelector('#brazil-map-svg svg')
      if (!svgElement) return

      const estados = svgElement.querySelectorAll('g[id]')

      estados.forEach(estado => {
        const estadoId = estado.id

        // Adiciona eventos
        estado.addEventListener('mouseenter', e => this.handleEstadoHover(e, estadoId))
        estado.addEventListener('mousemove', this.handleMouseMove)
        estado.addEventListener('mouseleave', this.handleMouseLeave)
        estado.addEventListener('click', () => this.handleEstadoClick(estadoId))

        // Adiciona cursor pointer
        estado.style.cursor = 'pointer'
      })
    },
    aplicarCores() {
      const svgElement = document.querySelector('#brazil-map-svg svg')
      if (!svgElement || this.dadosGeograficos.length === 0) return

      const estados = svgElement.querySelectorAll('g[id]')
      const valores = this.dadosGeograficos.map(d => d.valor_total)
      const minValue = Math.min(...valores)
      const maxValue = Math.max(...valores)

      // Detecta se está no dark mode
      const isDarkMode = document.documentElement.classList.contains('dark-layout')

      // Cores para modo claro (azul claro ao azul escuro)
      const lightColors = [
        '#e3f2fd',
        '#bbdefb',
        '#90caf9',
        '#64b5f6',
        '#42a5f5',
        '#2196f3',
        '#1e88e5',
        '#1976d2',
        '#1565c0',
      ]

      // Cores para modo escuro (gradiente vibrante)
      const darkColors = [
        '#1a237e', // Azul escuro profundo
        '#283593', // Azul índigo
        '#303f9f', // Azul royal
        '#3949ab', // Azul médio
        '#3f51b5', // Azul índigo claro
        '#5c6bc0', // Azul lavanda
        '#7986cb', // Azul claro
        '#9fa8da', // Azul pálido
        '#c5cae9', // Azul muito claro
      ]

      const colors = isDarkMode ? darkColors : lightColors

      estados.forEach(estado => {
        const estadoId = this.getSiglaFromId(estado.id)
        const dadosEstado = this.dadosGeograficos.find(d => d.estado === estadoId)

        let cor = isDarkMode ? '#2a2e3f' : '#aaa' // Cor padrão (sem dados)

        if (dadosEstado) {
          const normalized = (dadosEstado.valor_total - minValue) / (maxValue - minValue || 1)
          const colorIndex = Math.min(Math.floor(normalized * colors.length), colors.length - 1)
          cor = colors[colorIndex]
        }

        const paths = estado.querySelectorAll('path')
        const strokeColor = isDarkMode ? '#000000' : '#ffffff'

        paths.forEach(path => {
          path.style.fill = cor
          path.style.stroke = strokeColor
          path.style.strokeWidth = '300'
          path.style.transition = 'all 0.3s ease'
        })

        // Adiciona hover effect
        estado.addEventListener('mouseenter', () => {
          paths.forEach(path => {
            path.style.filter = 'brightness(1.2) saturate(1.3)'
            path.style.strokeWidth = '600'
          })
        })

        estado.addEventListener('mouseleave', () => {
          paths.forEach(path => {
            path.style.filter = 'none'
            path.style.strokeWidth = '300'
          })
        })
      })
    },
    getSiglaFromId(id) {
      // Mapeia os IDs do SVG para as siglas dos estados
      const mapa = {
        // Nomes padrão
        Acre: 'AC',
        Alagoas: 'AL',
        Amapa: 'AP',
        Amazonas: 'AM',
        Bahia: 'BA',
        Ceara: 'CE',
        DistritoFederal: 'DF',
        EspiritoSanto: 'ES',
        Goias: 'GO',
        Maranhao: 'MA',
        MatoGrosso: 'MT',
        MatoGrossoDoSul: 'MS',
        MinasGerais: 'MG',
        Para: 'PA',
        Paraiba: 'PB',
        Parana: 'PR',
        Pernambuco: 'PE',
        Piaui: 'PI',
        RioDeJaneiro: 'RJ',
        RioGrandeDoNorte: 'RN',
        RioGrandeDoSul: 'RS',
        Rondonia: 'RO',
        Roraima: 'RR',
        SantaCatarina: 'SC',
        SaoPaulo: 'SP',
        Sergipe: 'SE',
        Tocantins: 'TO',
        // Variações do SVG com underscores e erros ortográficos
        RioGrand_do_Sul: 'RS', // Note: sem "e" no Rio Grande
        Bahina: 'BA', // Note: erro ortográfico no SVG
        BrasiliaDistritoFederal: 'DF',
        Rio_deJaneiro: 'RJ', // Com underscore
        MatoGrosso_do_Sul: 'MS', // Com underscores
        Fernando_de_Noronha: 'RN', // Fernando de Noronha representa RN no SVG
        // Outras variações possíveis
        Rio_de_Janeiro: 'RJ',
        Mato_Grosso: 'MT',
        Mato_Grosso_do_Sul: 'MS',
        Rio_Grande_do_Sul: 'RS',
        Rio_Grande_do_Norte: 'RN',
        Santa_Catarina: 'SC',
        Sao_Paulo: 'SP',
        Espirito_Santo: 'ES',
        Minas_Gerais: 'MG',
        // Variações lowercase
        acre: 'AC',
        alagoas: 'AL',
        amapa: 'AP',
        amazonas: 'AM',
        bahia: 'BA',
        bahina: 'BA', // erro ortográfico
        ceara: 'CE',
        Ceará: 'CE',
        distritofederal: 'DF',
        brasiliadistritofederal: 'DF',
        espiritosanto: 'ES',
        goias: 'GO',
        Goiás: 'GO',
        maranhao: 'MA',
        Maranhão: 'MA',
        matogrosso: 'MT',
        matogrossodosul: 'MS',
        minasgerais: 'MG',
        para: 'PA',
        Pará: 'PA',
        paraiba: 'PB',
        Paraíba: 'PB',
        parana: 'PR',
        Paraná: 'PR',
        pernambuco: 'PE',
        piaui: 'PI',
        Piauí: 'PI',
        riodejaneiro: 'RJ',
        riograndedonorte: 'RN',
        riograndedosul: 'RS',
        riogranddosul: 'RS', // sem "e"
        rondonia: 'RO',
        Rondônia: 'RO',
        roraima: 'RR',
        santacatarina: 'SC',
        saopaulo: 'SP',
        'São Paulo': 'SP',
        sergipe: 'SE',
        tocantins: 'TO',
        fernando_de_noronha: 'RN', // Fernando de Noronha representa RN
        fernandodenoronha: 'RN',
      }

      // Tenta com o ID original
      if (mapa[id]) return mapa[id]

      // Tenta com lowercase
      const idLower = id.toLowerCase()
      if (mapa[idLower]) return mapa[idLower]

      // Tenta remover espaços e acentos
      const idNormalized = id.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, '')
      if (mapa[idNormalized]) return mapa[idNormalized]

      // Se já for uma sigla válida (2 letras), retorna
      if (/^[A-Z]{2}$/.test(id)) return id

      return id
    },
    getNomeEstado(sigla) {
      const estados = {
        AC: 'Acre',
        AL: 'Alagoas',
        AP: 'Amapá',
        AM: 'Amazonas',
        BA: 'Bahia',
        CE: 'Ceará',
        DF: 'Distrito Federal',
        ES: 'Espírito Santo',
        GO: 'Goiás',
        MA: 'Maranhão',
        MT: 'Mato Grosso',
        MS: 'Mato Grosso do Sul',
        MG: 'Minas Gerais',
        PA: 'Pará',
        PB: 'Paraíba',
        PR: 'Paraná',
        PE: 'Pernambuco',
        PI: 'Piauí',
        RJ: 'Rio de Janeiro',
        RN: 'Rio Grande do Norte',
        RS: 'Rio Grande do Sul',
        RO: 'Rondônia',
        RR: 'Roraima',
        SC: 'Santa Catarina',
        SP: 'São Paulo',
        SE: 'Sergipe',
        TO: 'Tocantins',
      }
      return estados[sigla] || sigla
    },
    getEstadoDados(estadoId) {
      const sigla = this.getSiglaFromId(estadoId)
      return this.dadosGeograficos.find(d => d.estado === sigla)
    },
    handleEstadoHover(e, estadoId) {
      const dados = this.getEstadoDados(estadoId)
      if (dados) {
        this.tooltipEstado = this.getNomeEstado(dados.estado)
        this.tooltipVisible = true
      }
    },
    handleMouseMove(e) {
      this.tooltipX = e.clientX + 15
      this.tooltipY = e.clientY + 15
    },
    handleMouseLeave() {
      this.tooltipVisible = false
    },
    handleEstadoClick(estadoId) {
      const sigla = this.getSiglaFromId(estadoId)
      const dados = this.dadosGeograficos.find(d => d.estado === sigla)

      if (dados) {
        this.estadoSelecionado = dados
      } else {
        this.$bvToast.toast(`Sem dados disponíveis para ${this.getNomeEstado(sigla)}`, {
          title: 'Informação',
          variant: 'info',
          solid: true,
          autoHideDelay: 3000,
        })
      }
    },
  },
}
</script>

<style scoped>
.map-container {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

#brazil-map-svg {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

#brazil-map-svg >>> svg {
  width: 100%;
  height: auto;
  display: block;
}

.estado-info-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  height: 100%;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.estado-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e0e0e0;
}

.estado-nome {
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
  margin: 0;
}

.estado-sigla {
  font-size: 1rem;
  padding: 8px 16px;
}

.estado-dados {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.dado-item {
  display: flex;
  align-items: center;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.dado-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.dado-icon {
  width: 45px;
  height: 45px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  flex-shrink: 0;
}

.dado-content {
  flex: 1;
}

.dado-content small {
  display: block;
  font-size: 0.75rem;
  margin-bottom: 4px;
}

.dado-content h4 {
  font-size: 1.25rem;
  font-weight: bold;
}

.legenda-container {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.legenda-header {
  margin-bottom: 10px;
}

.gradient-bar {
  height: 20px;
  border-radius: 4px;
  background: linear-gradient(
    to right,
    #e3f2fd,
    #bbdefb,
    #90caf9,
    #64b5f6,
    #42a5f5,
    #2196f3,
    #1e88e5,
    #1976d2,
    #1565c0
  );
  margin-bottom: 5px;
}

.gradient-labels {
  display: flex;
  justify-content: space-between;
}

.map-tooltip {
  position: fixed;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 10px 15px;
  border-radius: 6px;
  pointer-events: none;
  z-index: 1000;
  font-size: 0.9rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Responsivo */
@media (max-width: 991px) {
  .estado-info-card {
    margin-top: 20px;
  }
}

/* Dark Mode */
.dark-layout .map-container {
  background: #161d31 !important;
}

.dark-layout #brazil-map-svg {
  background: #161d31 !important;
}

.dark-layout #brazil-map-svg >>> svg {
  background: #161d31 !important;
}

.dark-layout .estado-info-card {
  background: #283046 !important;
}

.dark-layout .estado-header {
  border-bottom-color: #3b4253 !important;
}

.dark-layout .estado-nome {
  color: #d0d2d6 !important;
}

.dark-layout .dado-item {
  background: #161d31 !important;
}

.dark-layout .legenda-container {
  background: #283046 !important;
}
</style>
